import { ref, computed } from 'vue'
import { useApi } from './useApi'

export function usePlannerStore(csrfToken) {
  const api = useApi(csrfToken)

  // State
  const projects = ref([])
  const currentProject = ref(null)
  const brief = ref('')
  const planCount = ref(10)
  const loading = ref(false)

  // Generation state
  const generating = ref(false)
  const streamingText = ref('')
  const generationError = ref(null)
  const parsedEntries = ref([])
  const generationDone = ref(false)
  let abortController = null

  // Saving state
  const saving = ref(false)
  const saveError = ref(null)
  const savedPublication = ref(null)

  const knowledgeItems = computed(() => currentProject.value?.knowledge_items || [])

  // Actions
  async function loadProjects() {
    loading.value = true
    try {
      projects.value = await api.get('/api/projects')
    } finally {
      loading.value = false
    }
  }

  async function selectProject(id) {
    loading.value = true
    try {
      currentProject.value = await api.get(`/api/projects/${id}`)
      // Reset generation state when switching projects
      resetGeneration()
    } finally {
      loading.value = false
    }
  }

  function resetGeneration() {
    streamingText.value = ''
    generationError.value = null
    parsedEntries.value = []
    generationDone.value = false
    savedPublication.value = null
    saveError.value = null
  }

  function getCsrfToken() {
    return csrfToken || document.querySelector('meta[name="csrf-token"]')?.content || ''
  }

  async function generatePlan() {
    if (!currentProject.value?.id || !brief.value.trim()) return

    resetGeneration()
    abortController = new AbortController()
    generating.value = true

    try {
      const response = await fetch(
        `/api/projects/${currentProject.value.id}/generate_plan`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
            'X-CSRF-Token': getCsrfToken(),
          },
          credentials: 'same-origin',
          body: JSON.stringify({ brief: brief.value, count: planCount.value }),
          signal: abortController.signal,
        }
      )

      if (!response.ok) {
        const text = await response.text()
        throw new Error(`Generation failed: ${response.status} ${text}`)
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop()

        for (const line of lines) {
          if (line.startsWith('event: ')) continue
          if (line.startsWith('data: ')) {
            const data = line.slice(6)
            try {
              const parsed = JSON.parse(data)
              if (typeof parsed === 'object' && parsed !== null && parsed.event) {
                if (parsed.event === 'done') {
                  generationDone.value = true
                  parsedEntries.value = parseEntries(streamingText.value)
                } else if (parsed.event === 'error') {
                  generationError.value = parsed.message
                }
                continue
              }
              if (typeof parsed === 'string') {
                streamingText.value += parsed
              }
            } catch {
              streamingText.value += data
            }
          }
        }
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        generationError.value = e.message
        console.error('Plan generation error:', e)
      }
    } finally {
      generating.value = false
      abortController = null
    }
  }

  function stopGeneration() {
    abortController?.abort()
  }

  function parseEntries(text) {
    const lines = text.split('\n')
    const entries = []
    const re = /^\d+\.\s+\*\*(.+?)\*\*\s*[—–\-]\s*(.+)/

    for (const line of lines) {
      const match = line.match(re)
      if (match) {
        entries.push({
          title: match[1].trim(),
          description: match[2].trim(),
        })
      }
    }
    return entries
  }

  function removeEntry(index) {
    parsedEntries.value.splice(index, 1)
  }

  function updateEntryTitle(index, title) {
    parsedEntries.value[index].title = title
  }

  function updateEntryDescription(index, description) {
    parsedEntries.value[index].description = description
  }

  async function savePlan(publicationName) {
    if (!currentProject.value?.id || !parsedEntries.value.length) return

    saving.value = true
    saveError.value = null
    try {
      const result = await api.post(
        `/api/projects/${currentProject.value.id}/publications`,
        {
          name: publicationName || `Plan: ${brief.value.slice(0, 50)}`,
          entries: parsedEntries.value,
        }
      )
      savedPublication.value = result
      return result
    } catch (e) {
      saveError.value = e.message
      console.error('Save plan error:', e)
    } finally {
      saving.value = false
    }
  }

  return {
    projects,
    currentProject,
    brief,
    planCount,
    loading,
    generating,
    streamingText,
    generationError,
    parsedEntries,
    generationDone,
    saving,
    saveError,
    savedPublication,
    knowledgeItems,
    loadProjects,
    selectProject,
    generatePlan,
    stopGeneration,
    resetGeneration,
    removeEntry,
    updateEntryTitle,
    updateEntryDescription,
    savePlan,
  }
}
